import React from 'react';
import type { QuoteActions } from '../../../../../primeX-underwriter/components/footer/constants';
import type { RaterXCoverage } from '../../../api/types/rater-x-preset';
import { hasBlockingWarning } from '../constants/blockingWarning';
import { useUnderwriterContext } from '../provider/UnderwriterProvider';

/**
 * Generalizes the ZSP-only registration pattern inlined in ModComponentLineItem.tsx into a
 * reusable hook keyed by `item.id` (unique per line item, unlike ZSP's composite
 * `${key}.${productType}` key). Any coverage whose warnings carry the shared blocking marker
 * (see `hasBlockingWarning`) registers as a UW action blocker for the given actions — no
 * per-coverage hardcoding required.
 *
 * The effect's cleanup always deregisters the item, so a blocker never outlives the line
 * item that raised it (e.g. it unmounts while still blocking).
 */
export const useRegisterBlockingWarning = (
  item: RaterXCoverage,
  actionsToBlock: QuoteActions[]
) => {
  const context = useUnderwriterContext();
  const blockersRef = context.state.uwActionBlockersRef;
  const isBlocking = hasBlockingWarning(item.warnings);

  React.useEffect(() => {
    const blockers = blockersRef?.current;
    if (!blockers) {
      return;
    }

    if (isBlocking) {
      blockers.set(item.id, { ...item, actionToBlock: actionsToBlock });
    } else {
      blockers.delete(item.id);
    }

    return () => {
      blockers.delete(item.id);
    };
  }, [blockersRef, item, isBlocking, actionsToBlock]);
};

export default useRegisterBlockingWarning;
